import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import type { ReportLabel } from '../../../rtk-apis/reports/reportLabels';

/**
 * Labels cell for the reports list.
 *
 * The problem it solves: receptionists download finalized reports and upload them into
 * Best Practice by hand, and nothing recorded which ones were already done — so they lost
 * track and either re-uploaded or missed one. This is that missing state, owned by the
 * clinic rather than inferred from Best Practice.
 *
 * The vocabulary is shared per clinic, so the picker offers what already exists and only
 * creates a new label when nothing matches — otherwise the column drifts into "uploaded",
 * "Uploaded", "uploded" within a week and stops being scannable at a glance.
 */

/** Chip palette. Keys are stored on the label; unknown keys fall back to slate. */
const PALETTE: Record<string, { bg: string; fg: string; border: string }> = {
  slate: { bg: 'rgba(148,163,184,0.16)', fg: '#cbd5e1', border: 'rgba(148,163,184,0.35)' },
  teal: { bg: 'rgba(45,212,191,0.16)', fg: '#5eead4', border: 'rgba(45,212,191,0.4)' },
  violet: { bg: 'rgba(167,139,250,0.16)', fg: '#c4b5fd', border: 'rgba(167,139,250,0.4)' },
  amber: { bg: 'rgba(251,191,36,0.16)', fg: '#fcd34d', border: 'rgba(251,191,36,0.4)' },
  rose: { bg: 'rgba(251,113,133,0.16)', fg: '#fda4af', border: 'rgba(251,113,133,0.4)' },
  sky: { bg: 'rgba(56,189,248,0.16)', fg: '#7dd3fc', border: 'rgba(56,189,248,0.4)' },
  green: { bg: 'rgba(74,222,128,0.16)', fg: '#86efac', border: 'rgba(74,222,128,0.4)' },
};
const COLOR_KEYS = Object.keys(PALETTE);
export const toneOf = (color?: string) => PALETTE[color || 'slate'] || PALETTE.slate;

/** Deterministic colour for labels created before a colour was chosen. */
export const autoColor = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return COLOR_KEYS[hash % COLOR_KEYS.length];
};

interface Props {
  reportId: string;
  /** Label ids currently on this report. */
  value: string[];
  /** The clinic's whole vocabulary. */
  labels: ReportLabel[];
  readOnly?: boolean;
  busy?: boolean;
  /** Shown as the dialog subtitle so the modal says which report is being labelled. */
  patientName?: string;
  onChange: (reportId: string, labelIds: string[]) => void;
  onCreateLabel: (name: string) => Promise<ReportLabel | null>;
  onRenameLabel: (labelId: string, name: string) => void;
  onDeleteLabel: (labelId: string) => void;
}

export default function ReportLabelsCell({
  reportId,
  value,
  labels,
  readOnly,
  busy,
  patientName,
  onChange,
  onCreateLabel,
  onRenameLabel,
  onDeleteLabel,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [menu, setMenu] = useState<{ el: HTMLElement; label: ReportLabel } | null>(null);
  const [renaming, setRenaming] = useState<ReportLabel | null>(null);
  const [renameText, setRenameText] = useState('');

  const byId = useMemo(() => new Map(labels.map(l => [l._id, l])), [labels]);
  const applied = useMemo(
    () => (value || []).map(id => byId.get(id)).filter(Boolean) as ReportLabel[],
    [value, byId]
  );

  const term = query.trim().toLowerCase();
  const matches = useMemo(
    () => (term ? labels.filter(l => l.name.toLowerCase().includes(term)) : labels),
    [labels, term]
  );
  const exactExists = labels.some(l => l.name.trim().toLowerCase() === term);

  const closePicker = () => {
    setPickerOpen(false);
    setQuery('');
    setRenaming(null);
  };

  const toggle = (labelId: string) => {
    const next = value?.includes(labelId)
      ? value.filter(id => id !== labelId)
      : [...(value || []), labelId];
    onChange(reportId, next);
  };

  const createAndApply = async () => {
    const name = query.trim();
    if (!name || creating) {
      return;
    }
    setCreating(true);
    try {
      const created = await onCreateLabel(name);
      if (created?._id) {
        onChange(reportId, [...(value || []), created._id]);
        setQuery('');
      }
    } finally {
      setCreating(false);
    }
  };

  const commitRename = () => {
    if (renaming && renameText.trim() && renameText.trim() !== renaming.name) {
      onRenameLabel(renaming._id, renameText.trim());
    }
    setRenaming(null);
  };

  const chipSx = (label: ReportLabel) => {
    const tone = toneOf(label.color || autoColor(label._id));
    return {
      height: 22,
      maxWidth: 112,
      bgcolor: tone.bg,
      color: tone.fg,
      border: `1px solid ${tone.border}`,
      fontSize: 11,
      fontWeight: 600,
      '& .MuiChip-deleteIcon': { color: tone.fg, fontSize: 14, '&:hover': { color: tone.fg } },
    };
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        onClick={e => e.stopPropagation()}
        sx={{ rowGap: 0.5 }}
      >
        {applied.map(label => (
          <Tooltip
            key={label._id}
            title={label.name}
          >
            <Chip
              size="small"
              label={label.name}
              onDelete={readOnly ? undefined : () => toggle(label._id)}
              sx={chipSx(label)}
            />
          </Tooltip>
        ))}

        {busy && (
          <CircularProgress
            size={12}
            sx={{ color: 'rgba(148,163,184,0.7)' }}
          />
        )}

        {!readOnly &&
          (applied.length ? (
            // Once chips are present the control collapses to a fixed 24px circle. It is
            // flexShrink:0 because the chips will happily consume the whole cell and push
            // it out of view — which is exactly what happened at two labels.
            <Tooltip title="Add label">
              <IconButton
                size="small"
                onClick={() => setPickerOpen(true)}
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  color: '#5eead4',
                  border: '1px dashed rgba(45,212,191,0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(45,212,191,0.16)',
                    borderColor: 'rgba(45,212,191,0.9)',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => setPickerOpen(true)}
              sx={{
                minWidth: 0,
                flexShrink: 0,
                px: 1,
                py: 0.1,
                fontSize: 11,
                textTransform: 'none',
                color: 'rgba(148,163,184,0.9)',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(148,163,184,0.12)' },
              }}
            >
              Add label
            </Button>
          ))}

        {readOnly && !applied.length && (
          <Typography sx={{ fontSize: 12, color: 'rgba(148,163,184,0.6)' }}>—</Typography>
        )}
      </Stack>

      {/* A modal rather than a row-anchored popover: the reports list is a scrolling
          container, so an anchored panel was clipped by the row beneath it and covered
          the next patient's data. A dialog also matches the assign-doctor flow. */}
      <Dialog
        open={pickerOpen}
        onClose={closePicker}
        maxWidth="xs"
        fullWidth
        onClick={e => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#0b1b2b',
              backgroundImage: 'none',
              border: '1px solid rgba(148,163,184,0.22)',
              borderRadius: 3,
              boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2.5,
            pt: 2,
            pb: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>Labels</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'rgba(148,163,184,0.8)' }}>
              {patientName || 'Report'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={closePicker}
            sx={{ color: 'rgba(148,163,184,0.9)' }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              bgcolor: 'rgba(148,163,184,0.10)',
              border: '1px solid rgba(148,163,184,0.18)',
              '&:focus-within': { borderColor: 'rgba(45,212,191,0.55)' },
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: 'rgba(148,163,184,0.8)' }} />
            <InputBase
              autoFocus
              placeholder="Search or create…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && term && !exactExists) {
                  createAndApply();
                }
              }}
              sx={{ flex: 1, fontSize: 13, color: '#e2e8f0' }}
            />
          </Box>
        </Box>
        <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />

        <Box sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5, px: 1 }}>
          {matches.map(label => {
            const isOn = value?.includes(label._id);
            const tone = toneOf(label.color || autoColor(label._id));
            const isRenaming = renaming?._id === label._id;
            return (
              <Box
                key={label._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  '&:hover': { bgcolor: 'rgba(148,163,184,0.1)' },
                }}
              >
                {isRenaming ? (
                  <InputBase
                    autoFocus
                    value={renameText}
                    onChange={e => setRenameText(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        commitRename();
                      }
                      if (e.key === 'Escape') {
                        setRenaming(null);
                      }
                    }}
                    sx={{ flex: 1, fontSize: 12, color: '#e2e8f0' }}
                  />
                ) : (
                  <>
                    <Box
                      onClick={() => toggle(label._id)}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        minWidth: 0,
                      }}
                    >
                      <CheckIcon
                        sx={{ fontSize: 14, color: isOn ? tone.fg : 'transparent', flexShrink: 0 }}
                      />
                      <Chip
                        size="small"
                        label={label.name}
                        sx={chipSx(label)}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={e => setMenu({ el: e.currentTarget, label })}
                      sx={{ color: 'rgba(148,163,184,0.8)' }}
                    >
                      <MoreHorizIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </>
                )}
              </Box>
            );
          })}

          {term && !exactExists && (
            <Box
              onClick={createAndApply}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(45,212,191,0.12)' },
              }}
            >
              {creating ? (
                <CircularProgress
                  size={14}
                  sx={{ color: '#2dd4bf' }}
                />
              ) : (
                <AddIcon sx={{ fontSize: 15, color: '#2dd4bf' }} />
              )}
              <Typography sx={{ fontSize: 12, color: '#e2e8f0' }}>
                Create “{query.trim()}”
              </Typography>
            </Box>
          )}

          {!matches.length && !term && (
            <Typography sx={{ px: 1.5, py: 1.5, fontSize: 12, color: 'rgba(148,163,184,0.7)' }}>
              No labels yet. Type a name to create the first one.
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />
        <DialogActions sx={{ px: 2.5, py: 1.25, justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 11, color: 'rgba(148,163,184,0.65)' }}>
            {labels.length} label{labels.length === 1 ? '' : 's'} · shared across the clinic
          </Typography>
          <Button
            onClick={closePicker}
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: 12.5,
              fontWeight: 600,
              color: '#0b1b2b',
              bgcolor: '#2dd4bf',
              px: 2,
              '&:hover': { bgcolor: '#5eead4' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        open={!!menu}
        anchorEl={menu?.el}
        onClose={() => setMenu(null)}
        onClick={e => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#0b1b2b',
              border: '1px solid rgba(148,163,184,0.22)',
              color: '#e2e8f0',
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menu) {
              setRenaming(menu.label);
              setRenameText(menu.label.name);
            }
            setMenu(null);
          }}
          sx={{ fontSize: 13 }}
        >
          <ListItemIcon>
            <DriveFileRenameOutlineIcon sx={{ fontSize: 16, color: 'rgba(148,163,184,0.9)' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) {
              onDeleteLabel(menu.label._id);
            }
            setMenu(null);
          }}
          sx={{ fontSize: 13, color: '#fda4af' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon sx={{ fontSize: 16, color: '#fda4af' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>Remove from clinic</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
